import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon, IndeterminateProgressBar } from "@nexus/ui";

import { formatDurationMs } from "@/features/opportunities/lib/format-relative-time";
import { translateScriptReviewStatusLabel } from "@/features/script-review/lib/script-review-status";
import type { ScriptReviewStatus } from "@/features/script-review/api/script-review.api";

function useElapsedMs(startedAt: number | undefined, active: boolean): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !startedAt) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, startedAt]);

  if (!active || !startedAt) return null;
  return Math.max(0, now - startedAt);
}

interface ScriptReviewAnalysisProgressProps {
  startedAt: number;
  status?: ScriptReviewStatus;
  loading?: boolean;
}

export function ScriptReviewAnalysisProgress({
  startedAt,
  status,
  loading = false,
}: ScriptReviewAnalysisProgressProps) {
  const { t } = useTranslation();
  const elapsedMs = useElapsedMs(startedAt, startedAt > 0);
  const statusLabel = status ? translateScriptReviewStatusLabel(t, status) : null;

  return (
    <div
      className="border-border bg-muted/20 flex flex-col gap-3 rounded-lg border px-4 py-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <Icon
          name="loader"
          size="sm"
          className="text-primary mt-0.5 shrink-0 animate-spin"
          aria-hidden
        />
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-medium text-foreground">
            {loading
              ? t("scriptReview.loading", "Loading…")
              : t("scriptReview.inProgressTitle", "Analysis in progress")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              "scriptReview.inProgress",
              "Step outputs will appear here once analysis completes. Analysis usually takes a few minutes.",
            )}
          </p>
        </div>
      </div>
      <IndeterminateProgressBar />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {statusLabel ? (
          <span>
            {t("scriptReview.statusLabel", "Status")}: {statusLabel}
          </span>
        ) : null}
        {elapsedMs != null ? (
          <span>
            {t("scriptReview.analysisElapsed", "Elapsed: {{time}}", {
              time: formatDurationMs(elapsedMs),
            })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
